import { RefreshDouble } from 'iconoir-react'
import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import type { Provider } from 'app/generated/graphql'
import Combo from 'app/components/ui/combo/combo'
import SimpleTable from 'app/components/table/SimpleTable'
import type { OptionType } from 'types/OptionType'
import type { UseAvailabilityProvidersReturn } from './useAvailabilityProviders'

export type AvailabilityProvidersComponentProps = UseAvailabilityProvidersReturn

const STATUS_OPTIONS: OptionType[] = [
  { id: `enabled`, name: `Enabled`, value: `enabled` },
  { id: `disabled`, name: `Disabled`, value: `disabled` },
]

export function AvailabilityProvidersComponent(props: AvailabilityProvidersComponentProps) {
  const { rows, isSyncing, onSyncClick, onStatusChange } = props

  const columns = useMemo(
    (): ColumnDef<Provider, unknown>[] => [
      {
        header: `Name`,
        id: `displayName`,
        accessorFn: (row) => row.displayName,
        meta: {
          ControlledComponent: (row?: Provider) => (
            <div className="flex items-center px-2">
              <span className="overflow-hidden overflow-ellipsis whitespace-nowrap font-medium">
                {row?.displayName}
              </span>
            </div>
          ),
          defaultVisible: true,
        },
      },
      {
        header: `Scheduler`,
        id: `scheduler`,
        size: 140,
        meta: {
          ControlledComponent: (row?: Provider) => {
            if (!row) return <></>
            return (
              <div className="px-2">
                <Combo
                  inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-xs"
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((o) => o.id === (row.deactivated ? `disabled` : `enabled`))}
                  onChange={(val: OptionType) => onStatusChange(row.id, val.id === `disabled`)}
                />
              </div>
            )
          },
          defaultVisible: true,
        },
      },
    ],
    [onStatusChange],
  )

  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 items-center justify-start">
        <button className="btn btn-xs btn-tertiary gap-2" disabled={isSyncing} onClick={onSyncClick}>
          <RefreshDouble className={isSyncing ? `animate-spin` : ``} />
          {` `}
          <p>Sync providers</p>
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <SimpleTable columns={columns} data={rows} noDataMessage="No providers found" hideFooter={true} />
      </div>
    </div>
  )
}
